// 1. Framework / library imports
import React, { useCallback, useMemo, useState } from "react";

// 2. Helpers & Hooks
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// 3. API & Types
import { getVehicleModelsAPI } from "services/api/catalog/vehicles/models/api";
import {
  VehicleModelRow,
} from "services/api/catalog/vehicles/models/types";
import { getVehicleManufacturerAPI } from "services/api/catalog/vehicles/manufacturers/api";
import { VehicleManufacturer } from "services/api/catalog/vehicles/manufacturers/types";
import { initialVehicleModelDataWithPagination } from "./consts";
import { DataWithPagination } from "services/api";

// 4. UI Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";
import { DropdownOption } from "../Dropdown/types";
import VehicleModelTooltip from "./components/VehicleModelTooltip";

// 5. Local Types
import { VehicleModelDropdownProps } from "./types";

const FIVE_MINUTES = 5 * 60 * 1000;

const VehicleModelDropdown: React.FC<VehicleModelDropdownProps> = (
  props: VehicleModelDropdownProps
) => {
  const {
    vehicleManufacturerId,
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    autoOpen
  } = props;

  const isDisabled = useMemo(() => {
    if (!vehicleManufacturerId) return true;
    if (disabled) return true;
    return false;
  }, [disabled, vehicleManufacturerId]);

  const [searchValue, setSearchValue] = useState<string>("");

  const { data, isLoading } = useDropdownCache<DataWithPagination<VehicleModelRow>>(
    buildDropdownCacheKey(DropdownCacheKey.VehicleModels, vehicleManufacturerId, searchValue),
    () => getVehicleModelsAPI(vehicleManufacturerId!, initialVehicleModelDataWithPagination.pagination, searchValue),
    FIVE_MINUTES,
    !!vehicleManufacturerId
  );

  const searchResults = (vehicleManufacturerId && data) ? data : initialVehicleModelDataWithPagination;

  // Marku model ne nosi, a tooltip je prikazuje — dohvaća se po id-u i dijeli keš s
  // ostalim dropdownima, pa je to najviše jedan zahtjev po marki.
  const { data: manufacturer } = useDropdownCache<VehicleManufacturer>(
    buildDropdownCacheKey(DropdownCacheKey.VehicleManufacturers, "detail", vehicleManufacturerId),
    () => getVehicleManufacturerAPI(vehicleManufacturerId!),
    FIVE_MINUTES,
    !!vehicleManufacturerId
  );

  const manufacturerName = manufacturer?.name ?? null;

  const handleSearchClick = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((name: string) => {
    setSearchValue(name);
  }, []);

  const formatYear = (yearFrom: number | null, yearTo: number | null): string => {
    if (yearFrom && yearTo) return `${yearFrom} - ${yearTo}`;
    if (yearFrom) return `${yearFrom} -`;
    return "";
  };

  const vehicleModelItems: DropdownOption[] = searchResults.data
    .slice()
    .sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;

      const yearA = a.yearFrom ?? 0;
      const yearB = b.yearFrom ?? 0;
      return yearA - yearB;
    })
    .map((i) => {
      const year = formatYear(i.yearFrom, i.yearTo);
      const displayName = i.manufacturerCode ? `${i.name} (${i.manufacturerCode})` : i.name;
      return {
        value: i.id,
        text: year ? (
          <div className="flex justify-between items-center w-full">
            <span>{displayName}</span>
            <span className="text-slate-400 text-xs">{year}</span>
          </div>
        ) : displayName,
        displayName,
        image: i.image,
        // Detalji na hover slike u popisu — redak ih ne stigne pokazati.
        tooltip: <VehicleModelTooltip model={i} manufacturerName={manufacturerName} />,
      };
    });

  // Odabrani model — isti tooltip stoji i nakon odabira, uz vrijednost u polju.
  const selectedModel = useMemo(
    () => searchResults.data.find((model) => model.id === value),
    [searchResults.data, value]
  );

  return (
    <>
      <DropdownAutocomplete
        label={label}
        name={name}
        disabled={isDisabled}
        required={required}
        placeholder={placeholder}
        value={value}
        direction={direction}
        width={width}
        height={height}
        options={vehicleModelItems}
        onChange={onChange}
        onSearchChange={handleSearchChange}
        onSearchFocus={handleSearchClick}
        error={error}
        autoOpen={autoOpen}
        isLoading={isLoading}
        tooltip={
          selectedModel ? (
            <VehicleModelTooltip
              model={selectedModel}
              manufacturerName={manufacturerName}
            />
          ) : undefined
        }
      />
    </>
  );
};

export default React.memo(VehicleModelDropdown);
